import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Trophy,
  AlertTriangle,
  Activity,
  Building2,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Share2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SERIES_COLORS = [
  "#065f46",
  "#0891b2",
  "#7c3aed",
  "#d97706",
  "#be123c",
  "#0f766e",
];

// Heatmap cell tone
function cellTone(v) {
  if (v === null || v === undefined)
    return "bg-stone-100 text-stone-300";
  if (v >= 95) return "bg-emerald-700 text-white";
  if (v >= 85) return "bg-emerald-500 text-white";
  if (v >= 75) return "bg-amber-400 text-stone-900";
  if (v >= 60) return "bg-orange-500 text-white";
  return "bg-rose-700 text-white";
}

function dayShort(iso) {
  if (!iso || iso.length < 10) return iso;
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return `há ${Math.floor(diff / 86400)}d`;
}

export default function CorporateDashboard() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("compliance");
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/corporate?days=${d}`);
      setData(data);
    } catch (err) {
      console.error("corporate", err);
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    setSharing(true);
    setShareResult(null);
    try {
      const { data } = await api.post("/dashboard/corporate/share", { days: 7 });
      setShareResult(data);
      if (data.recipients === 0) {
        toast.error(
          "Nenhum admin/gerente com telefone cadastrado. Atualize em /settings."
        );
      } else if (data.sent > 0) {
        toast.success(
          `Recap enviado via WhatsApp para ${data.sent} de ${data.recipients} destinatário${
            data.recipients === 1 ? "" : "s"
          }.`
        );
      } else {
        toast.error("Falha ao enviar. Verifique se o Twilio está configurado.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao compartilhar");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    load(days);
  }, [days]);

  const sortedUnits = useMemo(() => {
    if (!data?.units) return [];
    const arr = [...data.units];
    arr.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "executions") return b.executions - a.executions;
      if (sortBy === "open_occurrences") return b.open_occurrences - a.open_occurrences;
      return b.compliance - a.compliance;
    });
    return arr;
  }, [data?.units, sortBy]);

  if (loading && !data) {
    return (
      <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="corporate-loading">
        <div className="h-[200px] flex items-center justify-center text-sm text-stone-400 font-body gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando comparativo entre unidades…
        </div>
      </Card>
    );
  }
  if (!data) return null;

  // Build chart data: row per day, column per unit
  const seriesChartData = (data.day_keys || []).map((day, i) => {
    const row = { day };
    (data.series || []).forEach((s) => {
      row[s.unit_name] = s.points[i]?.compliance ?? null;
    });
    return row;
  });

  const noData = !data.units?.length;

  return (
    <section className="space-y-5" data-testid="corporate-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Corporativo
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1">
            Benchmark entre unidades
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger
              className="w-[150px] bg-white border-stone-300"
              data-testid="corporate-range"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="14">14 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="60">60 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={loading}
            className="border-stone-300"
            data-testid="corporate-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}
          </Button>
          <Button
            size="sm"
            onClick={share}
            disabled={sharing || !data?.units?.length}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="corporate-share"
            title="Envia recap dos últimos 7 dias aos admins/gerentes com telefone cadastrado"
          >
            {sharing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Share2 className="h-4 w-4 mr-2" />
            )}
            Enviar WhatsApp
          </Button>
        </div>
      </div>

      {noData && (
        <Card className="p-8 rounded-xl border-stone-200 bg-white text-center">
          <Building2 className="h-8 w-8 mx-auto text-stone-400 mb-3" />
          <p className="font-display font-bold tracking-tight">
            Cadastre unidades para ativar o Dashboard Corporativo
          </p>
          <p className="text-sm text-stone-500 font-body mt-1">
            Em <code>/units</code> você cadastra cada loja, restaurante ou cozinha.
            Depois, as execuções e ocorrências vão alimentar este comparativo.
          </p>
        </Card>
      )}

      {shareResult && (
        <Card className="p-4 rounded-xl border-emerald-200 bg-emerald-50/40" data-testid="corporate-share-result">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-md bg-emerald-700 text-white flex items-center justify-center shrink-0">
              <Share2 className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">
                Recap enviado para {shareResult.sent} de {shareResult.recipients} destinatário{shareResult.recipients === 1 ? "" : "s"}
                {shareResult.failed?.length > 0 && (
                  <span className="text-rose-700"> · {shareResult.failed.length} falha{shareResult.failed.length === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-body mt-2 mb-1">
                Pré-visualização
              </div>
              <pre className="text-xs font-body whitespace-pre-wrap bg-white border border-stone-200 rounded-md p-3 leading-relaxed max-h-56 overflow-y-auto">
                {shareResult.preview}
              </pre>
              <p className="text-[11px] text-stone-500 font-body mt-2">
                💡 O envio automático semanal ocorre toda segunda-feira às 09:00 (BRT) para admins e gerentes com telefone em <code>/settings</code>.
              </p>
            </div>
            <button
              onClick={() => setShareResult(null)}
              className="text-stone-400 hover:text-stone-700 text-xs font-body"
            >
              ✕
            </button>
          </div>
        </Card>
      )}

      {!noData && (
        <>
          {/* Row 1: KPI cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5 rounded-xl border-stone-200 bg-white">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-body">
                Conformidade média
              </div>
              <div className="font-display text-3xl font-extrabold tracking-tight mt-2 text-emerald-800">
                {data.average}%
              </div>
              <div className="text-xs text-stone-500 font-body mt-1">
                {data.units.length} unidade{data.units.length === 1 ? "" : "s"}
              </div>
            </Card>

            {data.best && (
              <Card className="p-5 rounded-xl border-emerald-200 bg-emerald-50/40">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-body">
                      Melhor unidade
                    </div>
                    <div className="font-display text-lg font-bold tracking-tight mt-2 line-clamp-1">
                      {data.best.unit_name}
                    </div>
                  </div>
                  <Trophy className="h-5 w-5 text-emerald-700 shrink-0" />
                </div>
                <div className="font-display text-2xl font-extrabold tracking-tight text-emerald-700 mt-1 flex items-baseline gap-1">
                  {data.best.compliance}%
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </Card>
            )}

            {data.worst && data.worst.unit_id !== data.best?.unit_id && (
              <Card className="p-5 rounded-xl border-rose-200 bg-rose-50/40">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-rose-700 font-body">
                      Atenção requerida
                    </div>
                    <div className="font-display text-lg font-bold tracking-tight mt-2 line-clamp-1">
                      {data.worst.unit_name}
                    </div>
                  </div>
                  <AlertTriangle className="h-5 w-5 text-rose-700 shrink-0" />
                </div>
                <div className="font-display text-2xl font-extrabold tracking-tight text-rose-700 mt-1 flex items-baseline gap-1">
                  {data.worst.compliance}%
                  <ArrowDownRight className="h-4 w-4" />
                </div>
              </Card>
            )}

            <Card className="p-5 rounded-xl border-stone-200 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-body">
                    Ocorrências em aberto
                  </div>
                  <div className="font-display text-3xl font-extrabold tracking-tight mt-2">
                    {data.global_occurrences}
                  </div>
                </div>
                <Activity className="h-5 w-5 text-amber-700 shrink-0" />
              </div>
              <div className="text-xs text-stone-500 font-body mt-1">
                Toda a rede
              </div>
            </Card>
          </div>

          {/* Multi-series line chart */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white">
            <h3 className="font-display font-bold tracking-tight mb-1">
              Conformidade por unidade (top {data.series.length} mais ativas)
            </h3>
            <p className="text-xs text-stone-500 font-body mb-3">
              Lacunas indicam dias sem execução
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={seriesChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="day" tickFormatter={dayShort} stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    borderRadius: 8,
                    borderColor: "#e7e5e4",
                    fontSize: 12,
                  }}
                  labelFormatter={dayShort}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                {(data.series || []).map((s, i) => (
                  <Line
                    key={s.unit_id}
                    type="monotone"
                    dataKey={s.unit_name}
                    stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Heatmap */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white overflow-hidden">
            <h3 className="font-display font-bold tracking-tight mb-1">
              Heatmap de conformidade
            </h3>
            <p className="text-xs text-stone-500 font-body mb-3">
              Verde escuro ≥95% · Verde 85% · Amarelo 75% · Laranja 60% · Vermelho &lt;60%
            </p>
            <div className="overflow-x-auto" data-testid="corporate-heatmap">
              <table className="text-[10px] border-separate" style={{ borderSpacing: 2 }}>
                <thead>
                  <tr>
                    <th className="text-left text-stone-500 font-body uppercase tracking-wider pr-3 sticky left-0 bg-white">
                      Unidade
                    </th>
                    {(data.heatmap?.day_keys || []).map((d) => (
                      <th
                        key={d}
                        className="text-stone-500 font-body font-normal w-8"
                        title={d}
                      >
                        {dayShort(d)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.heatmap?.rows || []).map((row) => (
                    <tr key={row.unit_id}>
                      <td className="font-display font-semibold text-sm pr-3 py-1.5 sticky left-0 bg-white whitespace-nowrap">
                        {row.unit_name}
                      </td>
                      {row.values.map((v, i) => (
                        <td
                          key={i}
                          className={`text-center font-display font-bold rounded ${cellTone(v)}`}
                          style={{ width: 36, height: 24 }}
                          title={v === null ? "Sem execução" : `${v}%`}
                        >
                          {v === null ? "—" : Math.round(v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Sortable unit table */}
          <Card className="p-5 rounded-xl border-stone-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold tracking-tight">Unidades</h3>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="corporate-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compliance">Maior conformidade</SelectItem>
                  <SelectItem value="executions">Mais execuções</SelectItem>
                  <SelectItem value="open_occurrences">Mais ocorrências</SelectItem>
                  <SelectItem value="name">Nome (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {sortedUnits.map((u) => (
                <div
                  key={u.id}
                  className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 border border-stone-200 rounded-md bg-stone-50/60"
                  data-testid={`corporate-row-${u.id}`}
                >
                  <div className="col-span-4 min-w-0">
                    <div className="font-display font-semibold truncate">{u.name}</div>
                    <div className="text-xs text-stone-500 font-body truncate">
                      {u.address || "—"}
                    </div>
                  </div>
                  <div className="col-span-2 text-sm font-body">
                    <div className="text-stone-500 text-[10px] uppercase tracking-wider">
                      Conformidade
                    </div>
                    <div
                      className={`font-display font-bold text-base ${
                        u.compliance >= 85
                          ? "text-emerald-700"
                          : u.compliance >= 70
                          ? "text-amber-700"
                          : "text-rose-700"
                      }`}
                    >
                      {u.compliance > 0 ? `${u.compliance}%` : "—"}
                    </div>
                  </div>
                  <div className="col-span-2 text-sm font-body">
                    <div className="text-stone-500 text-[10px] uppercase tracking-wider">
                      Execuções
                    </div>
                    <div className="font-display font-bold text-base">
                      {u.executions}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-stone-500 text-[10px] uppercase tracking-wider">
                      Em aberto
                    </div>
                    {u.open_occurrences > 0 ? (
                      <Badge className="bg-rose-100 text-rose-800 border-none">
                        {u.open_occurrences} ocorrência{u.open_occurrences === 1 ? "" : "s"}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                        ok
                      </Badge>
                    )}
                  </div>
                  <div className="col-span-2 text-right">
                    <div className="text-stone-500 text-[10px] uppercase tracking-wider">
                      Último check
                    </div>
                    <div className="font-body text-xs text-stone-700">
                      {timeAgo(u.last_activity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </section>
  );
}
